import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import LanguageSwitcher from '../../components/LanguageSwitcher';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { login } = useAuth();
  const { toast } = useToast();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await login({ email, password });
      toast.success(t('auth.loginSuccess'));
      navigate('/dashboard');
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('auth.invalidCredentials'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-navy-deep font-display overflow-hidden h-screen w-full relative flex items-center justify-center bg-pattern">
      {/* Language Switcher */}
      <div className="absolute top-6 right-6 z-20">
        <LanguageSwitcher variant="dark" dropDirection="down" />
      </div>

      {/* Glow Auras */}
      <div className="glow-aura" style={{ top: '-10%', left: '-10%' }} />
      <div className="glow-aura" style={{ bottom: '-10%', right: '-10%' }} />

      <div className="z-10 w-full max-w-[1200px] flex flex-col items-center px-6">
        {/* Logo & Brand */}
        <div className="mb-10 text-center flex flex-col items-center">
          <img src="/logo.png" alt="FoxygenSeo" className="h-12 object-contain mb-3" />
          <p className="text-slate-400 text-sm font-medium">{t('auth.tagline')}</p>
        </div>

        {/* Login Card */}
        <div className="glass-card w-full max-w-md p-10 rounded-2xl shadow-2xl">
          <div className="mb-8">
            <h2 className="text-slate-900 text-2xl font-bold">{t('auth.welcomeBack')}</h2>
            <p className="text-slate-600 text-sm mt-1">{t('auth.signInSubtitle')}</p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2" htmlFor="email">
                {t('auth.email')}
              </label>
              <div className="relative">
                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xl">
                  mail
                </span>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.emailPlaceholder')}
                  required
                  className="w-full bg-white/50 border border-slate-200 rounded-xl py-3 pl-10 pr-4 text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-semibold text-slate-700" htmlFor="password">
                  {t('auth.password')}
                </label>
                <span className="text-xs font-bold text-primary cursor-default">{t('auth.forgotPassword')}</span>
              </div>
              <div className="relative">
                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xl">
                  lock
                </span>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth.passwordPlaceholder')}
                  required
                  className="w-full bg-white/50 border border-slate-200 rounded-xl py-3 pl-10 pr-12 text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <span className="material-symbols-outlined text-xl">
                    {showPassword ? 'visibility_off' : 'visibility'}
                  </span>
                </button>
              </div>
            </div>

            {/* Remember Me */}
            <div className="flex items-center gap-2">
              <input
                id="remember"
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer"
              />
              <label className="text-sm text-slate-600 font-medium cursor-pointer" htmlFor="remember">
                {t('auth.rememberMe')}
              </label>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-electric-blue hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-4 rounded-xl shadow-lg shadow-blue-500/25 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
            >
              {isSubmitting ? t('auth.signingIn') : t('auth.signIn')}
              {!isSubmitting && (
                <span className="material-symbols-outlined text-xl">login</span>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
